/* SEMIchelin — 식권대장 제휴점 위에 쌓는 방문 기록.
 *
 * 불변식: stores.json 은 읽기만 한다. 리뷰는 SQL Connect 저장소에만 쓴다.
 * 주간 배치가 매장 목록을 통째로 갈아엎어도 기록은 store_id 로 다시 붙는다.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ChevronDown, CircleQuestionMark, Heart } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Toggle } from "@/components/ui/toggle";

import { Bubble, ClusterBubble } from "./components/Bubble.jsx";
import { Marker, OriginMarker } from "./components/Marker.jsx";
import { Overlay } from "./components/Overlay.jsx";
import { MapChrome, MapCorner, MapFrame, Reel, Toast } from "./components/Overlays.jsx";
import { MyLocation, MyLocationButton } from "./components/MyLocation.jsx";
import { Sheet } from "./components/Sheet.jsx";
import { StoreList } from "./components/StoreList.jsx";
import { LockButton, RandomButton } from "./components/RandomButtons.jsx";
import { RecenterButton } from "./components/RecenterButton.jsx";
import { UserButton } from "./components/UserButton.jsx";
import { useClusters } from "./hooks/useClusters.js";
import { CAN_LOCATE, useGeo } from "./hooks/useGeo.js";
import { askHeading } from "./hooks/useHeading.js";
import { useKakaoMap } from "./hooks/useKakaoMap.js";
import { useRoulette } from "./hooks/useRoulette.js";
import * as api from "./lib/api.js";
import { KAKAO_JS_KEY, ORIGIN } from "./lib/config.js";
import { NEW_TERM, categories, countNew, parseTerms, toggleTerm, visible } from "./lib/filter.js";
import { KAKAO_RADIUS_M, keywordSearchAny } from "./lib/kakao.js";
import * as R from "./lib/reviews.js";
import { cn, haversine, isClosed } from "./lib/util.js";

const SORTS = [
  ["distance", "거리순"],
  ["visits", "방문 많은 순"],
  ["recent", "최근 방문순"],
  ["name", "이름순"],
];

/* 첫 화면이 담는 반경. 80m/분으로 걸어서 2분 반, 캡처 50곳이 들어오던 그 범위다. */
const NEAR_M = 200;

/* 좁은 화면에서는 감추는 배지. 여덟 개가 두 줄로 접히면서 목록과 지도가 나눠 쓸 높이를
   50px 씩 먹었다 — 폰에서는 그 50px 이 카드 반 장이다. 다섯 개면 한 줄에 들어간다.
   무엇을 남길지는 건수로 정하지 않는다. '점심 뭐 먹지'에 답하는 순서다 —
   한식·일식·양식·중식·분식은 끼니고, 카페와 샐러드는 끼니를 고른 다음 이야기다.
   (카페는 125곳으로 두 번째로 많지만, 그래서 남길 이유가 되지는 않는다.)
   감추기만 할 뿐 검색은 그대로다. 검색창에 '카페'라고 치면 여전히 걸린다 — 배지는
   검색어를 채우는 지름길일 뿐이라(README 좌측 패널) 없앤다고 길이 막히지는 않는다. */
const NARROW_HIDDEN_CATS = new Set(["카페", "샐러드", "아시안"]);

/* Tailwind 의 `max-wide:` 와 **같은 경계**다(style.css 의 --breakpoint-wide: 900px).
   여기서만 픽셀을 다시 적는 이유: CSS 로는 못 하는 판단(지도를 옮길 것인가)이 하나
   있어서다. 두 값이 갈라지면 말풍선은 접혔는데 지도는 안 따라오는 폭이 생긴다.

   상수로 굳히지 않고 누를 때마다 물어본다(REDUCED_MOTION 과 다른 점). 폰은 가로세로가
   뒤집히고 좁은 창은 넓어진다 — 뜰 때 한 번 본 값으로 못박으면 그 뒤로 계속 틀린다. */
const NARROW = "(width < 900px)";

const TOOLBAR = "toolbar flex flex-wrap items-center gap-2";

/* 겹쳐서 접힌 자리를 대표할 한 곳 — 리뷰가 가장 많은 집이다(같으면 앞선 것, 곧 가까운 쪽).
   핀은 이 집의 **색**을 쓴다. 원 안의 숫자는 대표와 무관하게 접힌 곳의 수다(Marker.jsx).
   가장 많이 가본 집이 그 자리의 얼굴이고, 나머지는 눌러서 펴는 목록에 있다. */
function headliner(members, reviews) {
  return members.reduce((best, s) =>
    R.reviewOf(reviews, s.id).visits.length > R.reviewOf(reviews, best.id).visits.length ? s : best);
}

function centroid(stores) {
  const pts = stores.filter((s) => s.lat != null);
  if (!pts.length) return null;
  return [
    pts.reduce((a, s) => a + s.lat, 0) / pts.length,
    pts.reduce((a, s) => a + s.lng, 0) / pts.length,
  ];
}

/* 이 점이 화면 한가운데쯤 있는가. 보이는 범위 대비 비율로 잰다 — 미터로 못박으면
   확대해 놓으면 늘 '벗어남'이고 축소해 놓으면 늘 '가운데'다(useClusters 의 HUDDLE_PX
   가 픽셀인 것과 같은 이유). 0.12 는 전체 폭의 12%, 곧 가운데 24% 안쪽이다. */
const CENTERED = 0.12;
function atCenter(map, p) {
  const b = map.getBounds();
  const sw = b.getSouthWest();
  const ne = b.getNorthEast();
  const c = map.getCenter();
  return Math.abs(c.getLat() - p.lat) <= (ne.getLat() - sw.getLat()) * CENTERED
      && Math.abs(c.getLng() - p.lng) <= (ne.getLng() - sw.getLng()) * CENTERED;
}

export default function App() {
  const [raw, setRaw] = useState([]);
  const [meta, setMeta] = useState({});
  const [loadFailed, setLoadFailed] = useState(false);
  const [reviews, setReviews] = useState({});   // 서버에서 받아온다. 아래 이펙트 참조.
  const [lunchOnly, setLunchOnly] = useState(true);
  const [wantOnly, setWantOnly] = useState(false);
  /* 지도 고정 — 켜면 보이는 곳에서만 뽑고, 지도가 스스로 움직이지 않는다.
     자세한 사정은 components/RandomButtons.jsx 의 머리 주석에 있다. */
  const [locked, setLocked] = useState(false);
  const [sort, setSort] = useState("distance");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({ id: null, pan: false, seq: 0 });
  /* 펴놓은 겹친 핀(useClusters.js). 묶음의 id 이고, 한 번에 하나만 열린다. */
  const [clusterId, setClusterId] = useState(null);
  const [origin, setOrigin] = useState(null);
  const [kakao, setKakao] = useState({ ids: null, info: null });
  const [sheetId, setSheetId] = useState(null);
  /* 좁은 화면에서 목록을 펴놨나. **기본은 접힘이다** — 폰에서는 지도가 46dvh 밖에 안 돼서
     펴놓으면 둘 다 좁다. 넓은 화면에는 이 상태가 아무 영향도 없다(손잡이도 목록도 wide 에서
     항상 보인다). 그래서 창을 늘렸다 줄여도 다시 계산할 게 없다 — CSS 가 알아서 한다. */
  const [listOpen, setListOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);
  const [user, setUser] = useState(null);
  const [signingIn, setSigningIn] = useState(false);
  /* 사진을 상태로 두는 이유: 캐시는 로그인이 끝나야 채워지는데, onUser 는 그보다
     먼저 울린다. 렌더 중에 localStorage 를 읽으면 그 시점엔 아직 비어 있다. */
  const [photo, setPhoto] = useState(null);

  const mapEl = useRef(null);

  // ── 데이터 ───────────────────────────────────────────────────────
  /* 매장은 세 파일에 나뉘어 있고 어느 파일에 있느냐가 곧 상태다(collector/storefile.py).
     new_stores.json 은 '신규', closed_stores.json 은 '휴무' — 여기서 _new / _closed 를
     붙여 한 목록으로 합친다. 두 부속 파일은 없거나 비어 있어도 된다(빈 목록으로 친다).
     stores.json 만 못 읽으면 실패다. */
  useEffect(() => {
    const read = (name) => fetch(`data/${name}`, { cache: "no-store" }).then((res) => res.json());
    const part = (name) => read(name).then((p) => p?.places || []).catch(() => []);
    Promise.all([read("stores.json"), part("new_stores.json"), part("closed_stores.json")])
      .then(([main, fresh, closed]) => {
        setRaw([
          ...(main.places || []),
          ...fresh.map((p) => ({ ...p, _new: true })),
          ...closed.map((p) => ({ ...p, _closed: true })),
        ]);
        setMeta(main.meta || {});
      })
      .catch(() => setLoadFailed(true));
  }, []);

  /* 기본 2.2초. 부르는 쪽이 더 길게 달라고 할 수 있다 — '어디를 눌러서 무엇을 켜라'
     같은 안내는 읽고 따라가야 해서, 결과만 알리는 말과 같은 시간을 주면 읽다가 사라진다. */
  const toast = useCallback(
    (msg, ms = 2200) => setToastMsg(msg ? { msg, ms, n: Date.now() } : null), []);
  useEffect(() => {
    if (!toastMsg) return;
    const t = setTimeout(() => setToastMsg(null), toastMsg.ms);
    return () => clearTimeout(t);
  }, [toastMsg]);

  // ── 로그인 ───────────────────────────────────────────────────────
  /* 로그인은 지도 위 버튼이 시작한다(UserButton.jsx). 여기서 자동으로 팝업을 열면
     브라우저가 막는다 — 클릭에서 시작된 팝업만 통과한다.
     autoSignIn 은 에뮬레이터에서만 실제로 동작해서, 로컬 개발과 e2e 는 예전처럼
     버튼을 누르지 않아도 로그인된 상태로 시작한다. */
  useEffect(() => {
    const stop = api.onUser((u) => { setUser(u); setPhoto(api.photoOf(u)); });
    api.autoSignIn().catch(() => {});
    return stop;
  }, []);

  /** 로그인시키고 그 사람을 돌려준다. 실패·취소면 null — 부르는 쪽이 그걸 보고
      하려던 일을 계속할지 정한다(needUser 참조). */
  const signIn = useCallback(() => {
    setSigningIn(true);
    return api.signIn()
      .then((u) => { setPhoto(api.photoOf(u)); return u; })
      /* 팝업을 그냥 닫는 건 실패가 아니다 — 토스트를 띄우면 "취소했는데 왜 오류가
         뜨지"가 된다. 나머지는 원인을 화면에 적는다. "로그인하지 못했습니다" 한 줄로는
         팝업 차단인지 설정 누락인지 구별이 안 돼서 실제로 두 번 헤맸다. */
      .catch((e) => {
        if (api.isSignInCancel(e)) return null;
        console.error("sign-in failed", e);
        toast(`로그인하지 못했습니다 — ${api.signInError(e)}`);
        return null;
      })
      .finally(() => setSigningIn(false));
  }, [toast]);

  const signOut = useCallback(() => { api.signOut().catch(() => toast("로그아웃하지 못했습니다")); },
                              [toast]);

  // ── 리뷰 ─────────────────────────────────────────────────────────
  /* 로그인되면 팀 전원의 방문 기록을 받아온다(찜만 내 것이다). 로그아웃하면 비운다.
     취소 플래그를 쓰면 안 된다 — useKakaoMap 과 같은 함정이다. StrictMode 는 첫
     이펙트를 즉시 청소하고 다시 실행하는데, 그 사이 응답이 오면 결과가 통째로
     버려지고 두 번째 실행은 가드에 막혀 아무 일도 안 한다 → 기록이 영영 안 뜬다.
     (실제로 밟았다: DB 에는 들어가는데 화면은 '방문 0'.)

     uid 가드는 남기되 **진행 중인 약속 자체를 들고 있는다.** 이유는 부르는 곳이 둘로
     늘었기 때문이다 — 아래 이펙트와, 로그인을 먼저 시키고 이어서 쓰는 needUser.
     끝났는지만 표시하면 두 번째 호출이 첫 요청을 기다리지 못하고 그냥 지나가는데,
     그러면 목록 응답이 방금 쓴 것을 덮어써서 '남겼는데 사라지는' 순간이 생긴다. */
  const loading = useRef(null);   // { uid, p }
  const loadReviews = useCallback((uid) => {
    if (loading.current?.uid === uid) return loading.current.p;
    const p = (async () => {
      // localStorage 시절 기록이 남아 있으면 한 번만 올린다. 원본은 지우지 않고
      // 이름만 바꿔둔다 — 별점 환산이 서버에서 확정되므로 되돌릴 길을 남긴다.
      const legacy = localStorage.getItem(R.LEGACY_KEY);
      if (legacy) {
        try {
          await api.importReviews(JSON.parse(legacy));
          localStorage.setItem(`${R.LEGACY_KEY}:migrated`, legacy);
          localStorage.removeItem(R.LEGACY_KEY);
        } catch { /* 실패하면 키를 남겨둔다. 다음에 열 때 다시 시도한다. */ }
      }
      try {
        setReviews(await api.listReviews());
      } catch {
        toast("기록을 불러오지 못했습니다");
      }
    })();
    loading.current = { uid, p };
    return p;
  }, [toast]);

  useEffect(() => {
    if (!user) { setReviews({}); loading.current = null; return; }
    loadReviews(user.uid);
  }, [user, loadReviews]);

  /* 리뷰를 건드리는 조작은 전부 여기를 먼저 지난다 — 안 돼 있으면 로그인부터 시킨다.
     리뷰는 로그인 없이는 읽지도 쓰지도 못한다(연산이 전부 @auth(level: USER)). 그래서
     로그아웃 상태로 시트를 열면 남의 리뷰가 하나도 없는 것처럼 '리뷰 0건'이 뜨고,
     등록을 눌러도 "저장하지 못했습니다"만 나온다 — 둘 다 사실이 아닌데 화면은 그렇게 말한다.

     **팝업은 클릭과 같은 태스크에서 열려야 한다.** 그래서 signIn() 앞에 await 를 하나도
     두지 않는다(lib/firebase.js 의 signIn 주석). 이 함수를 클릭 핸들러에서 바로 부르는
     것이 규칙이고, 이펙트 안에서 부르면 그 팝업은 조용히 막힌다.

     로그인 뒤에는 목록을 다 받고 나서 하려던 일을 잇는다. 안 기다리면 이펙트가 띄운
     목록 응답이 나중에 도착해 방금 쓴 한 건을 덮는다. */
  const needUser = useCallback(() => {
    if (user) return Promise.resolve(true);
    return signIn().then(async (u) => {
      if (!u) return false;   // 취소했다. 하려던 일도 조용히 접는다.
      await loadReviews(u.uid);
      return true;
    });
  }, [user, signIn, loadReviews]);

  /* 리뷰 쓰기는 전부 여기를 지난다.
     예전에는 상태가 바뀔 때마다 전부 localStorage 에 다시 썼다. 그 방식을 저장소로
     그대로 가져오면 하트 한 번에 498행이 갱신된다 — 그래서 바뀐 매장 하나만 보낸다.
     api.js 가 쓴 뒤 그 매장만 다시 읽어 돌려주고, null 이면(방문도 찜도 없으면)
     키를 지운다. localStorage 시절 mutate() 가 하던 삭제 판정과 방문 정렬을 조립
     질의로 옮긴 것이다 — 두 곳에서 정하면 반드시 어긋난다.
     실패를 조용히 삼키면 '남긴 줄 알았는데 안 남아 있다'가 되므로 반드시 알린다. */
  const applyReview = useCallback((id, call) => needUser()
    .then((ok) => ok && call()
      .then((review) => setReviews((prev) => {
        const next = { ...prev };
        if (review) next[id] = review; else delete next[id];
        return next;
      }))
      .catch(() => toast("저장하지 못했습니다"))), [needUser, toast]);

  /* 리뷰 시트로 가는 길. 여는 것부터 로그인을 거친다 — 로그인 전에는 목록이 통째로
     비어 보이므로, 열어놓고 '리뷰 0건'을 보여주느니 먼저 로그인시키는 게 정직하다.
     카드의 '리뷰 N건'과 말풍선의 '리뷰하기' 둘 다 여기로 온다. */
  const openReviews = useCallback(
    (id) => { needUser().then((ok) => { if (ok) setSheetId(id); }); }, [needUser]);

  const toggleWant = useCallback(
    (id) => applyReview(id, () => api.toggleWant(id)), [applyReview]);

  // ── 거리 기준점 ──────────────────────────────────────────────────
  // 우선순위: .env 수동 지정 > 지오코더가 역산한 캡처 시점 현위치 > 매장 중심점.
  // 역산 원점은 앱이 준 거리와 정확히 맞는 유일한 기준점이다.
  useEffect(() => {
    if (!raw.length) return;
    const geo = meta.origin;
    const o = ORIGIN || (geo && [geo.lat, geo.lng]) || centroid(raw);
    if (o) setOrigin(o);
  }, [raw, meta]);

  /* 예전에는 VITE_ORIGIN 이 비었을 때 여기서 브라우저 위치를 몰래 물어 기준점을 바꿨다.
     지웠다. 이유가 둘이다. (1) 아무 맥락 없이 뜬 권한 창은 거절당하기 쉽고, 한 번
     거절당하면 코드로는 다시 물어볼 수 없어서 아래 '내 위치' 버튼까지 같이 죽는다.
     (2) 그 코드는 거리 기준점을 내 위치로 바꿔놨는데, 좌표 없는 매장은 앱이 준 회사
     기준 거리를 그대로 쓰므로 한 목록에 두 기준이 섞였다. 현위치는 이제 지도에 점으로만
     나온다(components/MyLocation.jsx). */

  // 좌표가 있으면 실제 거리를, 없으면 앱이 준 거리를 쓴다.
  const stores = useMemo(() => raw.map((s) => ({
    ...s,
    _dist: origin && s.lat != null
      ? haversine(origin[0], origin[1], s.lat, s.lng)
      : s.distance_m ?? null,
  })), [raw, origin]);

  const hasCoords = useMemo(() => stores.some((s) => s.lat != null), [stores]);
  const center = useMemo(() => origin || centroid(stores), [origin, stores]);
  const { map, places, notice } = useKakaoMap(mapEl, {
    center, apiKey: KAKAO_JS_KEY, hasCoords,
  });

  // ── 내 위치 ──────────────────────────────────────────────────────
  /* 지도에 점을 찍을 뿐이다 — 거리와 정렬은 회사 기준 그대로다(MyLocation.jsx 머리 주석).
     지도가 내 자리로 가는 건 **누를 때뿐**이다: 켠 직후 첫 좌표에 한 번(useGeo 의 onFix),
     그리고 켜진 채로 다시 누를 때(아래 onToggleMe). 갱신마다 따라가면 목록을 보려고
     옮겨둔 화면을 몇 초에 한 번씩 뺏는다. */
  const panToMe = useCallback((p) => {
    if (map) map.panTo(new window.kakao.maps.LatLng(p.lat, p.lng));
  }, [map]);
  const { pos: me, tracking: meOn, toggle: toggleMe } =
    useGeo({ onFix: panToMe, onError: toast });

  /* 나침반 권한은 위치 권한과 별개다(iOS). 여기서 같이 받아야 한다 — 클릭과 같은
     태스크에서 물어야 통과하는 것이 팝업과 같아서, 이펙트로 미루면 조용히 거절된다.
     끌 때는 안 묻는다. */
  /* **위치를 먼저 부른다.** 둘 다 같은 클릭 안에서 나가야 하지만(그래야 iOS 가 사용자
     제스처로 쳐준다) 순서는 위치가 앞이다 — 나침반 권한은 iOS 에서 모달을 띄우는데,
     그게 먼저 올라가면 뒤따르는 위치 요청이 그 창에 가려 거절로 떨어질 수 있다.
     방향은 덤이고 위치가 본론이라, 하나가 밀린다면 밀릴 쪽은 나침반이다.
     toggleMe 도 askHeading 도 동기라 사이에 await 이 없다. 있으면 둘 다 막힌다. */
  /* 켜져 있는데 점이 화면 밖으로 밀려나 있으면, 누른 건 끄자는 게 아니라 **거기로
     돌아가자**는 뜻이다. 지도를 끌면 목록이 따라오는 앱이라(위 bounds 주석) 켜놓고
     주변을 훑는 일이 잦은데, 그때마다 내 점을 손으로 되찾아야 했다 — 껐다 켜는 게
     유일한 방법이었고 그건 GPS 를 다시 잡느라 몇 초를 버린다.
     이미 가운데면 그때는 끄는 게 맞다. 돌아갈 데가 없으니 남은 뜻이 그것뿐이다.
     좌표를 아직 못 잡았을 때(me 가 null)도 끈다 — 찾는 중인 걸 취소하는 길이다. */
  const onToggleMe = useCallback(() => {
    if (meOn && me && map && !atCenter(map, me)) return panToMe(me);
    toggleMe();
    if (!meOn) askHeading();   // 켜는 순간에만 — meOn 은 누르기 전 상태다
  }, [meOn, me, map, panToMe, toggleMe]);

  // ── 카카오 장소 검색 ─────────────────────────────────────────────
  // 상호명만 훑는 로컬 검색으로는 '돈까스', '마라탕' 같은 메뉴 키워드를 못 잡는다.
  // 카카오 색인은 그걸 알고 있으므로, 카카오에 물어본 결과를 우리 제휴점과
  // place_id 로 교집합 낸다. 로컬 매칭은 네트워크를 안 타서 즉시 반응한다.
  useEffect(() => {
    /* '신규'는 우리 목록에만 있는 말이라 카카오에 묻지 않는다. 물으면 늘 0건이 돌아와
       "카카오에도 신규 결과가 없습니다" 배너가 뜨는데, 정작 로컬에서는 찾아낸 뒤다 —
       찾아놓고 못 찾았다고 말하는 화면이 된다. */
    const needles = parseTerms(q).filter((t) => t !== NEW_TERM);
    if (!places || !origin || !needles.length) return setKakao({ ids: null, info: null });
    let cancelled = false;
    const timer = setTimeout(() => {
      keywordSearchAny(places, needles, origin).then(({ ids, total, truncated }) => {
        if (!cancelled) setKakao({ ids, info: { total, truncated } });
      });
    }, 300);   // 카카오는 타이핑이 멎은 뒤에 한 번만 부른다
    return () => { cancelled = true; clearTimeout(timer); };
  }, [q, places, origin]);

  const rows = useMemo(
    () => visible(stores, reviews, { q, lunchOnly, wantOnly, sort, kakaoIds: kakao.ids }),
    [stores, reviews, q, lunchOnly, wantOnly, sort, kakao.ids]);

  // ── 지도에 보이는 범위 ───────────────────────────────────────────
  /* 목록은 지금 지도에 보이는 곳만 담는다. 428곳짜리 목록에서 '거리순 1등'을 봐도,
     정작 화면은 다른 동네를 비추고 있으면 그 둘이 서로 다른 이야기를 한다. 지도를 끌면
     목록이 따라오는 게 이 앱의 답이다 — 어디를 보고 있느냐가 곧 질문이다.
     idle 로 듣는다. bounds_changed 는 드래그 한 번에 수십 번 울려서, 손을 놓을 때까지
     목록이 매 프레임 다시 그려진다. */
  const [bounds, setBounds] = useState(null);
  useEffect(() => {
    if (!map) return;
    const sync = () => setBounds(map.getBounds());
    sync();   // 첫 화면. idle 만 기다리면 지도가 뜬 직후 잠깐 전체 목록이 보인다.
    window.kakao.maps.event.addListener(map, "idle", sync);
    return () => window.kakao.maps.event.removeListener(map, "idle", sync);
  }, [map]);

  /* 지도 상자 크기가 바뀌면 카카오에게 알려준다(relayout).
     SDK 는 **창** 크기만 스스로 듣는다. 목록을 접었다 펴서 상자만 줄어드는 건 모르고
     지나가는데, 그러면 옛 크기 그대로 타일을 그려서 상자 밖으로 삐져나오고(실측:
     문서가 화면보다 111px 길어졌다) getBounds() 도 옛 범위를 답한다. 그 범위로 목록을
     거르니 화면과 목록이 어긋난다 — 눈에 보이는 건 넘친 타일이지만 진짜 문제는 이쪽이다.
     ResizeObserver 로 듣는 이유: 접기 말고도 상자가 바뀌는 길이 여럿이다(창 크기,
     wide 경계 넘나들기, 폰 가로세로 전환). 원인마다 따로 붙이면 하나씩 빠뜨린다. */
  useEffect(() => {
    if (!map || !mapEl.current) return;
    const ro = new ResizeObserver(() => { map.relayout(); setBounds(map.getBounds()); });
    ro.observe(mapEl.current);
    return () => ro.disconnect();
  }, [map]);

  /* 지도가 없으면 거르지 않는다. 키가 없거나 SDK 를 못 불러도 목록과 기록은 전부
     동작해야 한다(useKakaoMap 의 안내문이 그렇게 약속하고 있다) — 그때 범위로 걸러버리면
     목록이 통째로 비어서, 지도가 안 뜬 게 아니라 앱이 고장 난 것처럼 보인다. */
  const inView = useMemo(() => {
    if (!bounds) return rows;
    return rows.filter((s) => s.lat != null
      && bounds.contain(new window.kakao.maps.LatLng(s.lat, s.lng)));
  }, [rows, bounds]);

  const cats = useMemo(() => categories(raw), [raw]);
  const newCount = useMemo(() => countNew(raw), [raw]);
  // 눌린 칩은 검색어에서 읽는다. 별도 상태로 두면 검색창을 직접 고쳤을 때 둘이 어긋난다 —
  // 검색어가 유일한 진실이고 칩은 그걸 편집하는 손잡이다.
  const picked = useMemo(() => new Set(parseTerms(q.toLowerCase())), [q]);

  // ── 선택 ─────────────────────────────────────────────────────────
  /* 한 곳을 고르면 펴놨던 묶음은 접는다. 말풍선 둘이 동시에 뜨면 지도가 안 보이고,
     둘 중 어느 것이 지금 고른 것인지도 알 수 없다. 반대쪽도 같다(showCluster). */
  const select = useCallback((id, pan) => {
    setClusterId(null);
    setSelected((s) => ({ id, pan, seq: s.seq + 1 }));
  }, []);
  const showCluster = useCallback((id) => { select(null, false); setClusterId(id); }, [select]);

  /* 지도에서 고르는 길(핀, 그리고 겹친 핀 목록의 한 줄). 좁은 화면에서만 그 자리로 옮긴다.
     폰은 지도가 46dvh 뿐이라 가장자리 핀을 누르면 말풍선이 화면 밖으로 자란다 — 읽으려면
     지도를 밀어야 하고, 미는 순간 목록이 따라 바뀐다(inView). 가운데로 데려오면 그 왕복이
     통째로 없어진다. 넓은 화면은 지도가 넓어서 옮길 이유가 없고, 옮기면 오히려 보고 있던
     주변이 사라진다 — 핀은 지금 보이는 것들 중에 고르는 물건이다.
     목록 카드는 여기 안 온다. 저쪽은 넓은 화면에서도 늘 옮긴다(StoreCard 의 onSelect) —
     지도 밖에서 고른 것이라 어디 있는지부터 보여줘야 한다. */
  const pickPin = useCallback((id) => select(id, matchMedia(NARROW).matches), [select]);
  const selectedStore = useMemo(
    () => stores.find((s) => s.id === selected.id) || null, [stores, selected.id]);
  // 좌표가 없는 곳은 지도에 찍을 수 없으니 상세 시트로 보낸다.
  const bubbleStore = map && selectedStore?.lat != null ? selectedStore : null;

  // ── 겹친 마커 ────────────────────────────────────────────────────
  // 한 핀 = 한 묶음이다. 멤버가 하나뿐인 묶음이 곧 예전의 그 마커다(useClusters.js).
  const clusters = useClusters(map, rows, selected.id);
  const openCluster = useMemo(
    () => clusters.find((c) => c.id === clusterId && c.members.length > 1) || null,
    [clusters, clusterId]);

  /* 여기서는 openReviews 를 쓰지 않는다. 이펙트는 클릭과 다른 태스크라 여기서 연 팝업은
     브라우저가 막고, 그러면 시트도 안 열려서 "눌렀는데 아무 일도 안 일어난다"가 된다.
     로그아웃 상태로 열린 시트는 등록·찜을 누르는 순간 applyReview 가 로그인시킨다. */
  useEffect(() => {
    if (selected.id && !bubbleStore) setSheetId(selected.id);
    // 선택이 일어난 순간에만 판단한다. 지도가 늦게 떠도 시트를 다시 열지 않는다.
  }, [selected.seq]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!map || !selected.pan || !selected.id) return;
    const s = stores.find((x) => x.id === selected.id);
    if (s?.lat != null) map.panTo(new window.kakao.maps.LatLng(s.lat, s.lng));
    // stores 가 바뀔 때마다 다시 패닝하면 사용자가 움직인 화면을 뺏는다.
  }, [selected, map]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!map) return;
    const close = () => select(null, false);
    window.kakao.maps.event.addListener(map, "click", close);
    return () => window.kakao.maps.event.removeListener(map, "click", close);
  }, [map, select]);

  // ── 지도 범위 ────────────────────────────────────────────────────
  // 제휴점이 원점 가까이 몰려 있어서 기본 줌으로 열면 마커가 한 덩어리가 된다.
  // 검색이 바뀔 때마다 한 번씩만 맞추고, 그 뒤엔 사용자가 움직인 화면을 뺏지 않는다.
  //
  // 기본 화면은 걸어갈 만한 반경만 담는다. 수집 범위가 187m 에서 511m 로 넓어지자
  // 전부 담느라 배율이 두 단계 물러나 정작 앞 골목이 안 보였다. 먼 핀은 화면 밖에
  // 두는 게 낫다 — 나머지 451곳을 마저 채워도 처음 배율이 흔들리지 않는다.
  // 검색 중일 때는 안 자른다. 멀어도 찾던 그 가게를 보여줘야 한다.
  const fitted = useRef(false);
  useEffect(() => { fitted.current = false; }, [q, kakao.info, wantOnly]);
  useEffect(() => {
    if (!map || fitted.current) return;
    // 고정해뒀으면 검색 결과에 맞추느라 화면을 옮기지도 않는다. 지도가 스스로 움직이지
    // 않는 게 고정의 전부다. 맞춰뒀다고 표시해두는 이유는 나중에 풀었을 때 밀린 이동이
    // 뒤늦게 튀어나오지 않게 하기 위해서다.
    if (locked) { fitted.current = true; return; }
    let pts = rows.filter((s) => s.lat != null);
    if (!q.trim()) {
      const near = pts.filter((s) => s._dist != null && s._dist <= NEAR_M);
      if (near.length >= 2) pts = near;
    }
    if (!pts.length) return;
    /* 한 곳뿐이면 그 자리로 옮기기만 한다. 예전에는 여기서 그냥 빠져나갔다 — 목록이
       지도와 무관하던 시절에는 그래도 왼쪽에 그 한 줄이 남아 있었으니까. 지금은 목록이
       보이는 범위만 담아서, 안 옮기면 검색 결과 한 곳이 지도에도 목록에도 없다.
       setBounds 를 쓰지 않는 이유: 점이 하나면 범위가 넓이 0 이라 최대 배율까지 파고든다. */
    if (pts.length === 1) {
      map.panTo(new window.kakao.maps.LatLng(pts[0].lat, pts[0].lng));
      fitted.current = true;
      return;
    }
    const bounds = new window.kakao.maps.LatLngBounds();
    pts.forEach((s) => bounds.extend(new window.kakao.maps.LatLng(s.lat, s.lng)));
    if (origin) bounds.extend(new window.kakao.maps.LatLng(origin[0], origin[1]));
    map.setBounds(bounds, 70, 40, 40, 40);
    fitted.current = true;
  }, [map, rows, origin, q, locked]);

  // ── 랜덤 룰렛 ────────────────────────────────────────────────────
  /* 고정해뒀으면 당첨돼도 그 자리로 옮기지 않는다(pan). 보던 화면이 그대로 남아야
     마음에 안 들 때 곧바로 다시 돌릴 수 있다 — 한 번 뽑을 때마다 지도가 딴 데로
     가버리면 다음 판의 후보가 매번 달라진다. */
  const spinSelect = useCallback((id, pan) => {
    toast(null);   // 릴과 토스트는 같은 자리다. 둘이 겹치지 않게 치운다.
    select(id, pan && !locked);
  }, [select, toast, locked]);

  /* 고정해뒀으면 누른 순간의 지도 범위로 후보를 자른다. 지도를 옮겨가며 "여기서
     아무거나"를 하고 싶은데 목록 전체에서 뽑으면 1km 밖이 당첨된다.
     범위는 상태로 두지 않는다 — 후보를 함수로 넘기는 이유는 useRoulette 주석에 있다.
     좌표가 없는 곳은 화면 안에 있다고 말할 수가 없으니 여기서 빠진다.
     휴무(closed_stores.json)는 지도·목록에는 배지를 달고 남기지만 여기서는 뺀다 —
     "오늘 여기 가자"가 문 닫은 집으로 나오면 다시 돌려야 한다. */
  const candidates = useCallback(() => {
    const open = rows.filter((s) => !isClosed(s));
    if (!locked || !map) return open;
    const bounds = map.getBounds();
    return open.filter(
      (s) => s.lat != null && bounds.contain(new window.kakao.maps.LatLng(s.lat, s.lng)));
  }, [rows, locked, map]);

  const { spin, spinning, reel, rollingId, winnerId } = useRoulette({
    candidates,
    select: spinSelect,
    toast,
    // 왼쪽 목록은 가득한데 "고를 곳이 없습니다"만 뜨면 왜인지 알 수가 없다.
    emptyMsg: locked ? "이 화면에는 고를 곳이 없습니다" : undefined,
  });

  // 브라우저 검증(tools/e2e.py)이 지도를 들여다보는 유일한 손잡이.
  // 지도 상태는 React 밖(카카오 SDK)에 있어서 DOM 만으로는 확인할 수가 없다.
  // rollingId 를 같이 내보내는 이유: 룰렛이 훑는 후보가 화면 밖이면 카카오가 그 마커를
  // DOM 에서 빼버린다. 마커가 없는 게 버그인지 그냥 화면 밖인지 구분하려면 후보가 누구인지
  // 알아야 한다. 이게 없으면 지도를 어디로 옮겨뒀느냐에 따라 테스트가 들쭉날쭉해진다.
  // reviews 도 같이 내보낸다. 영속성 검사가 새로고침 뒤에 이걸 읽어서 "서버까지
  // 갔다 왔는가"를 본다 — DB 를 직접 들여다보지 않고도 왕복을 확인할 수 있다.
  // clusters 도 같이 낸다 — 마커가 이제 1:1 이 아니라서, 화면의 핀 수만으로는
  // '몇 곳이 그려졌나'를 셀 수 없다. 접힌 곳까지 세려면 묶음을 봐야 한다.
  // me 도 같이 낸다 — 현위치 점은 카카오 오버레이라 DOM 만 봐서는 '켜졌는데 좌표를
  // 못 잡은 것'인지 '아예 안 켜진 것'인지 구별이 안 된다.
  useEffect(() => {
    window.__app = { map, places, stores, origin, me, rollingId, reviews, clusters };
  }, [map, places, stores, origin, me, rollingId, reviews, clusters]);

  // ── 화면 ─────────────────────────────────────────────────────────
  /* 이 숫자는 '팀이 어디까지 가봤나'다. 내 방문만 세면 새로 들어온 사람에게는 늘 0이고,
     정작 알고 싶은 것(우리가 아직 안 뚫은 집이 몇인가)은 아무 데도 안 나온다. */
  const active = stores.filter((s) => !(lunchOnly && s.lunch_candidate === false));
  const visited = active.filter((s) => R.reviewOf(reviews, s.id).visits.length).length;
  const pct = active.length ? Math.round((visited / active.length) * 100) : 0;
  const totalInApp = meta.total_in_app;

  const sheetStore = useMemo(
    () => stores.find((s) => s.id === sheetId) || null, [stores, sheetId]);

  return (
    <div className="app flex h-dvh flex-col">
      {/* 그리드 아이템의 기본 min-width:auto 는 nowrap 콘텐츠(긴 상호명, 배지)에 밀려
          컨테이너를 넘긴다. 좁은 화면에서 가로 스크롤이 생기는 원인 — min-w-0 로 막는다. */}
      {/* 세로로 쌓이는 동안 지도가 남는 자리를 전부 갖는다(auto 1fr). 이게 없으면 행이
          둘 다 auto 라 남는 높이가 반씩 갈리고, 목록을 접었을 때 그만큼이 패널 아래
          빈 배경으로 남는다. 넓은 화면은 한 행이라 grid-rows 를 도로 풀어준다. */}
      <main className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[auto_1fr]
                       wide:grid-cols-[minmax(320px,400px)_1fr] wide:grid-rows-none">
        {/* 제목·검색·필터는 리스트 위에만 둔다. 지도까지 가로지르면 지도가 좁아진다. */}
        <aside className="panel flex min-h-0 min-w-0 flex-col border-r border-border bg-background">
          {/* 예전엔 화면 전체를 가로지르는 헤더였다. 오른쪽에 놓을 게 없어지자
              (내보내기·가져오기를 내렸다) 지도 위 한 줄을 통째로 먹는 값만 남았다.
              좁은 폭에 한 줄로 넣으면 통계가 접히니 두 줄로 쌓는다. */}
          <div className="phead flex flex-col gap-px p-[11px_14px_3px]">
            <div className="flex items-center gap-2">
              <span className="text-base font-bold tracking-[-.01em]">
                <span className="text-brand">SEMI</span>chelin
              </span>

              {/* 데이터가 틀렸을 때 어디로 말해야 하는지. 제목 줄 끝에 둔다 —
                  늘 보이지만 읽을 것은 하나도 늘지 않는 자리다.
                  hover 색을 못박는 이유: ghost 변형의 hover:text-accent-foreground 가
                  흰색이라, 안 덮으면 흰 패널 위에서 아이콘이 사라진다. */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost" size="icon-xs" id="btn-help"
                    aria-label="정보 수정 문의"
                    className="ml-auto size-5 text-muted-foreground hover:bg-transparent
                               hover:text-primary"
                  >
                    <CircleQuestionMark className="size-4" />
                  </Button>
                </PopoverTrigger>
                {/* break-keep(word-break:keep-all) 이 없으면 한글은 글자 단위로 잘려서
                    '주세요' 가 '주 / 세요' 로 갈라진다. 어절 사이에서만 끊게 한다. */}
                <PopoverContent align="end"
                                className="w-auto max-w-[220px] break-keep p-3 text-[12.5px] leading-relaxed">
                  잘못 나오거나 빠진 음식점 정보는 DM으로 알려주세요
                </PopoverContent>
              </Popover>
            </div>
            <span className="stats text-xs leading-[1.45] text-muted-foreground" id="stats">
              미방문 <b className="font-bold text-primary">{active.length - visited}</b>
              {` · 방문 ${visited} / ${active.length} (${pct}%)`}
            </span>
          </div>

          <div className={cn(TOOLBAR, "p-[10px_14px_8px]")}>
            <Input
              className="search h-auto w-full rounded-[7px] bg-background px-[11px] py-1.5 text-[13.5px] shadow-none"
              id="search" type="search" autoComplete="off"
              placeholder="메뉴·상호 검색 (마라탕 + 샐러드)"
              value={q} onChange={(e) => setQ(e.target.value)}
            />
          </div>

          {/* 카카오맵 '주변 탐색'처럼, 자주 쓰는 키워드를 바로 누를 수 있게.
              여러 개를 눌러도 된다 — 검색어에 ' + ' 로 이어 붙어 OR 로 걸린다. */}
          <div className="cats flex flex-wrap gap-1.5 p-[0_14px_9px]" id="cats">
            {cats.map(({ label, n }) => {
              const on = picked.has(label.toLowerCase());
              return (
                <Toggle
                  key={label}
                  variant="pill"
                  size="pill"
                  /* 감추는 것뿐이라 눌러둔 채로 창을 좁혀도 필터는 그대로 걸려 있다.
                     그게 맞다 — 검색어가 유일한 진실이고 배지는 그걸 편집하는 손잡이일
                     뿐이라(아래 picked 주석), 손잡이가 안 보인다고 값이 풀리면 안 된다.
                     검색창에 남은 글자가 무엇이 걸려 있는지 계속 말해준다. */
                  className={cn(
                    `cat items-baseline gap-[5px] border-border px-[11px] py-1 text-[12.5px]
                     hover:border-primary hover:text-primary`,
                    NARROW_HIDDEN_CATS.has(label) && "hidden wide:inline-flex",
                  )}
                  data-cat={label}
                  pressed={on}
                  onPressedChange={(next) => setQ(toggleTerm(q, label, next))}
                >
                  {label}
                  <span className={cn("n text-[10.5px]", on ? "text-primary-soft" : "text-input")}>
                    {n}
                  </span>
                </Toggle>
              );
            })}
          </div>

          <div className={cn(TOOLBAR, "tools border-b border-border p-[0_14px_10px]")}>
            <Toggle
              variant="pill" size="pill" className="chip" id="chip-lunch"
              title="편의점·마트 제외"
              pressed={lunchOnly}
              onPressedChange={setLunchOnly}
            >
              점심만
            </Toggle>

            <Toggle
              variant="pill" size="pill" className="chip gap-1" id="chip-want"
              title="찜한 곳만"
              pressed={wantOnly}
              onPressedChange={setWantOnly}
            >
              <Heart className={cn("size-3", wantOnly && "fill-current")} />
              찜
            </Toggle>

            {/* 옆의 점심만·찜과 달리 이건 검색어를 채우는 손잡이다(카테고리 칩과 같은 방식).
                별도 boolean 으로 두면 검색창에 '신규'라고 직접 친 사람에게는 칩이 꺼져
                보인다 — 같은 필터가 걸렸는데 화면 두 곳이 다른 말을 하게 된다.
                수가 0이면 아예 안 그린다. 일주일 지나면 저절로 0이 되는 값이라,
                안 그러면 눌러도 늘 0곳인 죽은 칩이 남는다(filter.js countNew). */}
            {newCount > 0 && (
              <Toggle
                variant="pill" size="pill" className="chip gap-1" id="chip-new"
                title="최근 일주일 안에 들어온 곳"
                pressed={picked.has(NEW_TERM)}
                onPressedChange={(next) => setQ(toggleTerm(q, NEW_TERM, next))}
              >
                {NEW_TERM}
                <span className={cn("n text-[10.5px]",
                                    picked.has(NEW_TERM) ? "text-primary-soft" : "text-input")}>
                  {newCount}
                </span>
              </Toggle>
            )}

            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger
                className="sort h-auto rounded-[7px] border-input bg-background px-[9px] py-1.5
                           text-[13px] text-ink-2 shadow-none"
                id="sort" aria-label="정렬"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORTS.map(([v, label]) => (
                  <SelectItem key={v} value={v} className="text-[13px]">{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* 랜덤은 여기 없다 — 지도 위로 옮겼다(RandomButtons.jsx). */}
          </div>

          {/* 카카오 검색 결과 요약 — 몇 곳 중 제휴점이 몇 곳인지 */}
          <div
            className="searchinfo flex flex-wrap items-center gap-2 border-b border-border
                       bg-primary-soft p-[7px_14px] text-[12.5px] text-primary"
            id="searchinfo" hidden={!q.trim() || !kakao.info}
          >
            {q.trim() && kakao.info && (kakao.info.total === 0
              ? <span>{`카카오에도 "${q.trim()}" 결과가 없습니다`}</span>
              : <>
                  카카오 <b className="font-bold">{kakao.info.total}</b>곳 중 제휴점{" "}
                  <b className="font-bold">{rows.length}</b>곳
                  {kakao.info.truncated &&
                    <span className="text-muted-foreground">
                      {` · 반경 ${KAKAO_RADIUS_M}m 상위 ${kakao.info.total}건만 확인했습니다`}
                    </span>}
                </>)}
          </div>

          {/* 목록을 접는 손잡이 — 좁은 화면에만 있다. 넓은 화면은 목록과 지도가 나란히
              놓여서 접을 이유가 없다.
              지금 상태가 아니라 무엇이 접히는지를 적는다. 화살표가 방향을 말하고
              (접혔으면 아래 = 여기서 내려온다), 곳 수가 접힌 안에 무엇이 있는지 말한다 —
              필터를 걸어놓고 접었을 때 이 숫자가 유일한 단서다.
              지도와 맞닿는 자리라 아래 테두리로 갈라놓는다. */}
          <button
            aria-controls="list"
            aria-expanded={listOpen}
            className="listbtn flex w-full items-center justify-center gap-1.5 border-b
                       border-border bg-background py-2 text-[12.5px] font-semibold
                       text-ink-2 hover:bg-muted wide:hidden"
            id="btn-list"
            onClick={() => setListOpen((open) => !open)}
            type="button"
          >
            {listOpen ? "목록 접기" : `목록 ${inView.length}곳`}
            <ChevronDown
              className={cn("size-4 transition-transform", listOpen && "rotate-180")}
            />
          </button>

          {loadFailed
            ? <div
                className={cn("list flex flex-1 flex-col bg-ground p-2.5",
                              !listOpen && "hidden wide:flex")}
                id="list"
              >
                <p className="empty px-5 py-10 text-center text-sm text-muted-foreground">
                  data/stores.json 을 읽지 못했습니다. web/ 에서 npm run dev 로 띄워주세요.
                </p>
              </div>
            : <StoreList rows={inView} reviews={reviews} selectedId={selected.id}
                         /* 걸러지기 전 수도 같이 준다 — '없다'가 검색 탓인지 지도를
                            딴 데로 옮겨놓은 탓인지 빈 화면에서 구별해줘야 한다. */
                         matched={rows.length}
                         kakaoInfo={kakao.info} query={q} onSelect={select}
                         /* 접혀 있어도 넓은 화면에서는 늘 보인다 — 이 상태는 좁은 화면
                            전용이고, 그 구분을 CSS 한 줄에 맡긴다. */
                         className={cn(!listOpen && "hidden wide:flex")}
                         onOpenReviews={openReviews} onToggleWant={toggleWant} />}
        </aside>

        <MapFrame compact={listOpen} containerRef={mapEl} notice={notice}>
          <MapChrome className={cn(listOpen && "hidden wide:flex")}>
            <UserButton
              busy={signingIn}
              onSignIn={signIn}
              onSignOut={signOut}
              photo={photo}
              user={user}
            />
            {/* 회사로 되돌리기 — 좁은 화면에서는 접는다. 거리 기준점을 눈으로 대조하려고
                두는 버튼인데(RecenterButton 머리 주석) 그 대조는 지도와 목록의 숫자를
                번갈아 보는 일이라 넓은 화면의 일이다. 폰에서는 지도가 46dvh 뿐이라
                회사 표식이 화면 밖으로 나가도 목록을 조금 굴리면 그만이다.
                한 쌍이던 '내 위치'는 아래 모서리로 내려가서, 이제 혼자 선다. */}
            <RecenterButton map={map} origin={origin} className="max-wide:hidden" />

            {/* 주사위와 자물쇠는 한 세트다 — 간격을 좁혀 위쪽 버튼들과 갈라 놓는다.
                지도가 없으면 뽑을 화면도 없으니 랜덤도 없다.

                한동안 좁은 화면에서 감췄다가 되살렸다. 감춘 이유는 둘이었는데 둘 다
                틀렸다. (1) 이 열이 224px 이라 얇은 지도 밖으로 삐져나온다 — 지금은
                '회사로'와 '내 위치'가 빠져 132px 이라 46dvh 안에 든다. (2) 랜덤은
                넓은 화면의 놀이다 — 아니었다. 점심 뭐 먹을지 못 고르는 건 자리에 앉아
                큰 화면을 보고 있을 때가 아니라 폰 들고 나가는 길에 벌어지는 일이다. */}
            {map && (
              <div className="flex flex-col items-end gap-1">
                <RandomButton onSpin={spin} spinning={spinning} locked={locked} />
                <LockButton locked={locked} onChange={setLocked} />
              </div>
            )}
          </MapChrome>

          {/* '내 위치'만 아래 모서리에 산다 — 왜 갈라놨는지는 MapCorner 머리 주석.
              폰·태블릿에만 나온다(CAN_LOCATE). 데스크톱에서는 이 모서리가 통째로 없어서
              빈 상자도 안 잡는다 — 왜 그렇게 정했는지는 useGeo.js.
              busy — 켜긴 했는데 좌표는 아직인 사이. 이걸 안 보여주면 '켜짐'과 '찾음'이
              같은 그림이라 빈 지도를 고장으로 읽는다(MyLocation.jsx).
              목록을 펴는 동안 접는 것은 위 열과 같다. 넘칠 걱정은 없지만, 지도를 다루는
              손잡이라 목록을 보고 있는 동안 못 눌러도 아쉬울 게 없다. */}
          {map && CAN_LOCATE && (
            <MapCorner className={cn(listOpen && "hidden wide:flex")}>
              <MyLocationButton busy={meOn && !me} on={meOn} onToggle={onToggleMe} />
            </MapCorner>
          )}

          {origin && (
            <Overlay map={map} lat={origin[0]} lng={origin[1]} zIndex={0}>
              <OriginMarker />
            </Overlay>
          )}

          {me && <MyLocation map={map} pos={me} />}

          {clusters.map((c) => {
            const many = c.members.length > 1;
            const s = headliner(c.members, reviews);
            const open = c === openCluster;
            return (
              <Overlay key={c.id} map={map} lat={c.lat} lng={c.lng} clickable
                       zIndex={open || s.id === selected.id ? 10 : 1}>
                <Marker
                  store={s}
                  status={R.statusOf(reviews, s)}
                  stacked={c.members.length}
                  /* 접힌 핀은 목록이 펴져 있는 동안 '고른 것'으로 표시한다 —
                     말풍선이 어느 핀에서 나왔는지 그것 말고는 알 길이 없다. */
                  selected={open || s.id === selected.id}
                  /* 룰렛이 훑고 지나가는 곳이 접힌 채여도 그 자리가 튀어야 한다.
                     여기서 묶음을 풀지는 않는다 — rollingId 는 한 판에 열일곱 번
                     바뀌므로, 그때마다 다시 묶으면 핀 수백 개가 매번 재배치된다. */
                  rolling={c.members.some((m) => m.id === rollingId)}
                  winner={c.members.some((m) => m.id === winnerId)}
                  onSelect={many ? () => showCluster(c.id) : pickPin}
                />
              </Overlay>
            );
          })}

          {openCluster && (
            <Overlay map={map} lat={openCluster.lat} lng={openCluster.lng} zIndex={100} clickable>
              <ClusterBubble
                stores={openCluster.members}
                reviews={reviews}
                onPick={pickPin}
                onToggleWant={toggleWant}
                onClose={() => setClusterId(null)}
              />
            </Overlay>
          )}

          {bubbleStore && (
            <Overlay map={map} lat={bubbleStore.lat} lng={bubbleStore.lng} zIndex={100} clickable>
              <Bubble
                store={bubbleStore}
                rating={R.ratingOf(reviews, bubbleStore.id)}
                want={R.reviewOf(reviews, bubbleStore.id).want_to_go}
                onToggleWant={() => toggleWant(bubbleStore.id)}
                onClose={() => select(null, false)}
                onMore={() => openReviews(bubbleStore.id)}
              />
            </Overlay>
          )}

          {/* 릴과 토스트는 지도 안에 산다 — 가로 가운데를 지도에 맞추기 위해서다.
              (Overlays.jsx 의 PILL 주석) */}
          <Reel reel={reel} />
          <Toast toast={toastMsg} />
        </MapFrame>
      </main>

      <Sheet
        store={sheetStore}
        reviews={reviews}
        onClose={() => setSheetId(null)}
        /* 시트는 응답을 기다리지 않고 닫는다. 등록은 실패할 일이 거의 없고,
           기다리게 하면 탭 한 번으로 끝나던 기록이 아니게 된다.
           실패하면 applyReview 가 토스트로 알린다 — 소리 없이 사라지지는 않는다.
           '찜'을 끄는 것은 서버가 한다(POST visits 가 북마크를 지운다). */
        onSave={(id, visit) => {
          applyReview(id, () => api.addVisit(id, visit));
          setSheetId(null);
          toast("리뷰를 등록했습니다");
        }}
        /* 고치기도 같은 관문(applyReview)을 지난다 — 서버는 user_id 로 남의 기록을
           막고 있고(mutations.gql), 화면은 내 리뷰에만 연필을 그린다.
           여기서는 시트를 닫지 않는다. 고치러 온 사람은 고친 줄이 어떻게 바뀌었는지
           보러 온 것이라, 닫으면 확인하려고 다시 열어야 한다. */
        onUpdate={(id, visitId, visit) => {
          applyReview(id, () => api.updateVisit(id, visitId, visit));
          toast("리뷰를 고쳤습니다");
        }}
        /* 시트를 닫지 않는다. 찜이 시트 아래 버튼 줄에 있던 시절에는 '누르면 볼일 끝'
           이라 닫는 게 맞았는데, 지금은 제목 옆의 토글이다 — 리뷰를 쓰다 하트를 눌렀다고
           작성 중인 폼이 사라지면 안 된다. 닫는 것은 '등록'과 ×·Esc 뿐이다. */
        onWant={toggleWant}
        /* 배열 인덱스가 아니라 방문 id 로 지운다. 정렬을 서버가 정하게 된 이상
           인덱스는 화면에만 있는 값이라, 그걸로 지우면 목록이 한 번이라도 어긋났을 때
           엉뚱한 기록이 사라진다. */
        onDeleteVisit={(id, visitId) => applyReview(id, () => api.deleteVisit(id, visitId))}
      />
    </div>
  );
}
